<template>
  <div>
    <v-data-table
      :headers="headers"
      :items="items"
      :items-per-page="10"
      :footer-props="{
        'items-per-page-options': [10, 20, 50],
        'show-current-page': true,
        'show-first-last-page': true,
        'show-rows-per-page': true,
        'show-elevation': true,
      }"
      :loading="loading"
      no-data-text="Nenhum dado foi encontrado para essa combinação de categorias e lojas"
    >
      <template v-slot:item="{ item }">
        <tr>
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <td v-bind="attrs" v-on="on">{{ putEllipsis(item.unit.name, 18) }}</td>
            </template>
            <span> {{ item.unit.name }}</span>
          </v-tooltip>

          <v-tooltip
            v-for="(category, index) in categories"
            :key="`category-${item.unit.id_erp}-${category.id}`"
            content-class="custom-tooltip pa-5"
            open-delay="100"
            top
          >
            <template v-slot:activator="{ on, attrs }">
              <td
                class="hover-darken"
                :style="{
                  ...calculateBackgroundColor(item.values[category.id].changePercentage * 100 || 0),
                  cursor: 'pointer',
                  minWidth: '100px',
                }"
                :class="{ 'hovered-row': hoveredRow === item.unit.id_erp, 'hovered-col': hoveredCol === index }"
                v-on="on"
                @mouseover="hoverCell(item.unit.id_erp, index)"
                @mouseleave="leaveCell"
                @click="openCellInformation(item.unit, category, item.values[category.id])"
              >
                <span class="info-text">{{ item.values[category.id].current | genericFormatter(formatter) }}</span>
                <span class="percentage-text">{{ (item.values[category.id].changePercentage * 100 || 0) | localeString(2) }}%</span>
              </td>
            </template>
            <div>
              <h2 class="ma-0">{{ category.name }}</h2>
              <p class="unit-name mt-1 mb-4">{{ item.unit.name }}</p>
              <div class="difference d-flex align-center mt-0 mb-2" :class="getVariatClass(item.values[category.id])">
                <span class="my-0">
                  {{ cellInformationConfiguration.metric }}
                </span>

                <div class="arrow-box d-flex justify-center align-center mx-1">
                  <mf-icon size="12" color="branco" v-bind="getTooltipIcon((item.values[category.id].changePercentage * 100 || 0).toFixed(2))" />
                </div>

                <span class="my-0">{{ (item.values[category.id].changePercentage * 100) | localeString(2) }}%</span>
              </div>

              <p v-if="!removeReferenceData" class="period ma-0">
                <span>{{ item.values[category.id].reference | genericFormatter(formatter) }}</span> período referência
              </p>
              <p class="period mt-1 mb-4">
                <span>{{ item.values[category.id].current | genericFormatter(formatter) }}</span> período atual
              </p>
              <p class="call-to-action ma-0">Clique na célula para ver mais detalhes</p>
            </div>
          </v-tooltip>
        </tr>
      </template>
    </v-data-table>
    <cell-information-modal
      v-if="isCellInformationOpen"
      v-model="isCellInformationOpen"
      :configuration="cellInformationConfiguration"
      :unit="selectedCellInformationUnit"
      :category="selectedCellInformationCategory"
      :values="selectedCellInformationValues"
      :reference-date="referenceDate"
      :current-date="currentDate"
      :formatter="formatter"
      :quick-action="quickAction"
      :item-level-quick-action="itemLevelQuickAction"
      :remove-reference-data="removeReferenceData"
      :is-purchased-on-reference="isPurchasedOnReference"
    />
  </div>
</template>

<script>
import { putEllipsis, localeString } from '@/config/helpers.js'
import getCellColor from '../../mocks/cellColors'
export default {
  components: {
    CellInformationModal: () => import('@/modules/produtos/comparativo-categorias/views/components/CellInformationModal.vue'),
  },

  props: {
    units: {
      type: Array,
      default: () => [],
    },
    categories: {
      type: Array,
      default: () => [],
    },
    comparisonData: {
      type: Array,
      default: () => [],
    },
    metricKey: {
      type: String,
      default: '',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    formatter: {
      type: Object,
      default: () => ({}),
    },
    cellInformationConfiguration: {
      type: Object,
      default: () => ({}),
    },
    referenceDate: {
      type: Array,
      default: () => [],
    },
    currentDate: {
      type: Array,
      default: () => [],
    },
    quickAction: {
      type: Object,
    },
    itemLevelQuickAction: {
      type: Object,
    },
    customReferenceGetter: {
      type: Function,
    },
    customCurrentGetter: {
      type: Function,
    },
    customPercentageCalculator: {
      type: Function,
    },
    customColorValueCalculator: {
      type: Function,
    },
    isEmpty: {
      type: Boolean,
      default: false,
    },
    groupByUnits: {
      type: Boolean,
      default: false,
    },
    metricDescription: {
      type: String,
      default: '',
    },
  },

  data: () => ({
    isCellInformationOpen: false,
    selectedCellInformationUnit: {},
    selectedCellInformationCategory: {},
    selectedCellInformationValues: {},
    hoveredRow: null,
    hoveredCol: null,
  }),

  computed: {
    isPurchasedOnReference() {
      return this.cellInformationConfiguration?.metricKey === 'clients_purchased_only_on_reference'
    },
    isPurchasedOnCurrent() {
      return this.cellInformationConfiguration?.metricKey === 'clients_purchased_only_on_current'
    },
    removeReferenceData() {
      return this.isPurchasedOnReference || this.isPurchasedOnCurrent
    },
    headers() {
      return [
        { text: 'Loja', value: 'unit.name', width: '200px' },
        ...this.categories.map((category) => ({
          text: category.name,
          value: `values.${category.id}.changePercentage`,
          width: '170px',
        })),
      ]
    },
    items() {
      if (this.loading || this.isEmpty) return []
      const unitsArray = this.groupByUnits ? this.units : [{ id_erp: 'total', name: 'Total', mocked: true }]
      return unitsArray.map((unit) => {
        const unitMetrics = this.comparisonData.find((metric) => metric.unit_id === unit.id_erp)
        return {
          unit,
          values: this.categories.reduce((acc, category) => {
            const categoryUnitMetrics = unitMetrics?.category_metrics?.find((metric) => metric?.category_id === category.id)
            const categoryUnitReferenceMetricValue = this.customReferenceGetter
              ? this.customReferenceGetter(categoryUnitMetrics)
              : categoryUnitMetrics?.reference?.[this.metricKey]
            const categoryUnitCurrentMetricValue = this.customCurrentGetter
              ? this.customCurrentGetter(categoryUnitMetrics)
              : categoryUnitMetrics?.current?.[this.metricKey]
            const percentageCalculator = this.customPercentageCalculator || this.defaultPercentageCalculator
            acc[category.id] = {
              current: categoryUnitCurrentMetricValue || 0,
              reference: categoryUnitReferenceMetricValue || 0,
              changePercentage: percentageCalculator(categoryUnitReferenceMetricValue, categoryUnitCurrentMetricValue),
            }
            return acc
          }, {}),
        }
      })
    },
  },
  methods: {
    localeString,
    putEllipsis,
    exportData() {
      const baseHeaders = this.groupByUnits ? ['CNPJ da loja', 'Nome da loja'] : ['Total']
      const headers = [
        ...baseHeaders,
        ...this.categories.reduce(
          (acc, category) => [
            ...acc,
            `${category.name} - ${this.metricDescription} - Atual`,
            `${category.name} - ${this.metricDescription} - Referência`,
            `${category.name} - ${this.metricDescription} - Percentual de variação`,
          ],
          []
        ),
      ]
      const rows = this.items.map((item) => {
        const baseItem = this.groupByUnits ? [item.unit.cnpj, item.unit.name] : ['Total']
        return [
          ...baseItem,
          ...this.categories.reduce(
            (acc, category) => [...acc, item.values[category.id].current, item.values[category.id].reference, item.values[category.id].changePercentage],
            []
          ),
        ]
      })
      return {
        headers,
        rows,
      }
    },
    getVariatClass(value) {
      const newValue = (value.changePercentage * 100 || 0).toFixed(2)
      if (this.isPurchasedOnReference) return newValue === '0.00' ? '' : 'difference-negative'
      else if (newValue > 0 && !this.isPurchasedOnReference) return 'difference-positive'
      else if (newValue < 0) return 'difference-negative'
      return ''
    },
    getTooltipIcon(percentage) {
      if (percentage === '0.00') {
        return { icon: 'remove' }
      } else if (percentage < 0 || this.isPurchasedOnReference) {
        return { icon: 'arrow_downward' }
      } else {
        return { icon: 'arrow_upward' }
      }
    },
    hoverCell(rowId, colId) {
      this.hoveredRow = rowId
      this.hoveredCol = colId
    },
    leaveCell() {
      this.hoveredRow = null
      this.hoveredCol = null
    },
    openCellInformation(unit, category, values) {
      this.isCellInformationOpen = true
      this.selectedCellInformationUnit = unit
      this.selectedCellInformationCategory = category
      this.selectedCellInformationValues = values
    },
    defaultPercentageCalculator(referenceValue, currentValue) {
      let changePercentage
      if (referenceValue) {
        changePercentage = currentValue / referenceValue - 1
      } else if (currentValue) {
        changePercentage = 1
      } else {
        changePercentage = 0
      }
      return changePercentage
    },
    calculateBackgroundColor(value) {
      // Transform in negative to get red color
      if (this.isPurchasedOnReference) value = value * -1
      const { color, bg } = getCellColor(value.toFixed(2))
      return {
        backgroundColor: bg,
        color,
      }
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep .hover-darken.hovered-row,
.hover-darken.hovered-col {
  //background: red !important;
  filter: brightness(90%) !important;
}

::v-deep .hover-darken:hover {
  filter: brightness(80%) !important;
}

.info-text {
  font-size: 14px;
  font-weight: 600;
  display: block;
  line-height: 1.1;
}
.percentage-text {
  font-size: 12px;
  line-height: 1.1;
  display: block;
}

.custom-tooltip {
  background-color: $branco;
  opacity: 1 !important;
  border: 1px solid $cinza-l70;
  border-radius: 8px;

  h2 {
    font-size: 18px;
    line-height: 120%;
    font-weight: 400;
    color: $cinza-d20;
  }

  .unit-name {
    font-size: 14px;
    line-height: 150%;
    color: $cinza-0;
  }

  .difference {
    font-size: 16px;
    color: $cinza-0;
    font-weight: 600;
  }

  .difference {
    span {
      font-size: 16px;
      font-weight: 600;
      color: $cinza-0;
    }
  }

  .arrow-box {
    width: 20px;
    height: 20px;
    border-radius: 4px;
    background: $cinza-0;
  }

  .difference-negative {
    span {
      color: $vermelho-0;
    }

    .arrow-box {
      background-color: $vermelho-0;
    }
  }

  .difference-positive {
    span {
      color: $verde-0;
    }

    .arrow-box {
      background-color: $verde-0;
    }
  }

  .period {
    font-size: 14px;
    line-height: 150%;
    font-weight: 700;
    color: $cinza-d10;

    span {
      color: $marca-1;
    }
  }

  .call-to-action {
    font-weight: 700;
    font-size: 14px;
    color: $marca-1;
  }
}

::v-deep tr {
  position: relative;

  > th:first-child,
  > td:first-child {
    position: sticky;
    top: 0;
    left: 0;
    background: $branco;
    z-index: 1;
  }
}
</style>
